import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, BookOpen, Download, MagnifyingGlass, X, ShareNetwork, Hourglass, Scales, Sparkle, Cloud } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
const ICONS = { Hourglass, Scales, Sparkle, Cloud };

export default function GlossairePublic() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("all");

  useEffect(() => {
    axios.get(`${API}/public/apprentissage/glossary`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Erreur chargement"));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.glossary.filter((g) =>
      (activeCat === "all" || g.categorie === activeCat) &&
      (q === "" || g.terme.toLowerCase().includes(q) || g.definition.toLowerCase().includes(q))
    );
  }, [data, search, activeCat]);

  const downloadManuel = async () => {
    try {
      const r = await axios.get(`${API}/public/apprentissage/manuel.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "vita-lex-manuel-pratique.pdf"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Manuel téléchargé");
    } catch { toast.error("Erreur téléchargement"); }
  };

  const shareWA = () => {
    const url = window.location.href;
    const text = encodeURIComponent(`Glossaire VITA-LEX — Théorie Vitaliste des Finances Publiques · ${url}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };
  const shareTG = () => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent("Glossaire VITA-LEX · Théorie Vitaliste des Finances Publiques");
    window.open(`https://t.me/share/url?url=${url}&text=${text}`, "_blank");
  };

  if (!data) return <div className="p-12 label-mono">Chargement...</div>;

  return (
    <div className="min-h-screen bg-[var(--paper)]">
      <header className="border-b-2 border-[var(--signal)] bg-[var(--paper)]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-5 flex items-center justify-between flex-wrap gap-3">
          <Link to="/" className="flex items-center gap-3" data-testid="glossaire-back-home">
            <span className="font-mono text-xs tracking-[0.2em] text-[var(--muted)]">§ VITA-LEX</span>
            <span className="font-serif text-xl">ANALYTICA</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/auth" className="border border-black px-4 py-2 label-mono hover:bg-black hover:text-[var(--paper)]" data-testid="glossaire-cta-signup">Ouvrir un dossier →</Link>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 py-12">
        <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-mono">§ Référence publique · Accès libre</div>
            <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-medium tracking-tight flex items-center gap-3">
              <BookOpen size={40} weight="duotone" className="text-[var(--signal)]" /> Glossaire vitaliste
            </h1>
            <p className="mt-3 max-w-2xl text-[var(--muted)] leading-relaxed">
              {data.n_termes} termes pour parler couramment des finances publiques avec une grammaire vitaliste — libre de droits, citables dans vos articles, mémoires et plaidoyers.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={downloadManuel} data-testid="glossaire-download-manuel"
              className="bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] px-5 py-3 label-mono flex items-center gap-2">
              <Download size={14} weight="bold" /> Manuel pratique (PDF)
            </button>
            <button onClick={shareWA} className="border border-black px-3 py-3 hover:bg-black hover:text-[var(--paper)]" title="Partager sur WhatsApp" data-testid="glossaire-share-wa">
              <ShareNetwork size={14} />
            </button>
            <button onClick={shareTG} className="border border-black px-3 py-3 hover:bg-black hover:text-[var(--paper)]" title="Partager sur Telegram" data-testid="glossaire-share-tg">
              <ShareNetwork size={14} weight="fill" />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-6 items-center">
          <div className="flex items-center gap-2 border border-black px-3 py-2 flex-1 min-w-[260px] bg-[var(--paper)]">
            <MagnifyingGlass size={14} className="text-[var(--muted)]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un terme..."
              className="bg-transparent border-0 outline-0 flex-1 font-mono text-sm" data-testid="glossaire-search" />
            {search && <button onClick={() => setSearch("")}><X size={12} /></button>}
          </div>
          <button onClick={() => setActiveCat("all")} data-testid="glossaire-cat-all"
            className={`px-3 py-2 label-mono border ${activeCat === "all" ? "bg-[var(--ink)] text-[var(--paper)] border-black" : "border-black/30 hover:border-black"}`}>
            Tous
          </button>
          {Object.entries(data.categories).map(([id, meta]) => {
            const Icon = ICONS[meta.icone] || Sparkle;
            return (
              <button key={id} onClick={() => setActiveCat(id)} data-testid={`glossaire-cat-${id}`}
                className={`px-3 py-2 label-mono border flex items-center gap-2 ${activeCat === id ? "text-[var(--paper)] border-transparent" : "border-black/30 hover:border-black"}`}
                style={activeCat === id ? { background: meta.couleur } : {}}>
                <Icon size={12} weight={activeCat === id ? "fill" : "regular"} /> {meta.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/15 border border-black/15">
          {filtered.map((t) => {
            const cat = data.categories[t.categorie];
            return (
              <div key={t.terme} className="bg-[var(--paper)] p-5">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-serif text-xl text-[var(--signal)]">{t.terme}</div>
                  {cat && <span className="font-mono text-[9px] px-2 py-0.5 border" style={{ borderColor: cat.couleur, color: cat.couleur }}>{cat.label.split(" ")[0]}</span>}
                </div>
                <p className="mt-2 text-sm leading-relaxed">{t.definition}</p>
                {t.exemple && <p className="mt-2 text-xs italic text-[var(--muted)] border-l-2 border-black/20 pl-2">Ex. {t.exemple}</p>}
              </div>
            );
          })}
        </div>

        <div className="mt-12 border border-black/15 bg-[var(--paper)] p-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="label-mono">§ Aller plus loin</div>
            <h2 className="font-serif text-2xl mt-1">Mesurer votre situation citoyenne en 3 minutes</h2>
            <p className="mt-1 text-[var(--muted)]">Le moteur Vita-Engine convertit votre fiche de paie en jours de vie sacrifiés. Gratuit, sans publicité, sans tracking.</p>
          </div>
          <Link to="/auth" data-testid="glossaire-cta-bottom" className="bg-[var(--signal)] text-[var(--paper)] px-6 py-3 label-mono">Ouvrir mon dossier →</Link>
        </div>
      </main>
    </div>
  );
}
